
import { useState } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Modal } from './Modal';
import { useFormSubmission } from '../../hooks/useFormSubmission';
import ReCAPTCHA from "react-google-recaptcha";
import PhoneNumberInput from './PhoneNumberInput';
import { ToastContainer, toast } from 'react-toastify';

interface ContactFormProps {
  className?: string;
  title?: string;
  subtitle?: string;
}

export const ContactForm: React.FC<ContactFormProps> = ({ 
  className = '', 
  title = 'Entre em Contato',
  subtitle = 'Preencha o formulário e entraremos em contato'
}) => {
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    empresa: '',
    servico: '',
    mensagem: '',
    recaptchaToken: '' 
  });

  const [emailWarning, setEmailWarning] = useState<{ isValid: boolean; message: string; type: 'valid' | 'invalid' | 'personal' } | string>('');
  const [showModal, setShowModal] = useState(false);
  const { isLoading, isSuccess, isError, error, submitForm, resetForm } = useFormSubmission();

  // Lista de provedores de email pessoais (reduzida para os principais)
  const personalEmailProviders = [
    '@gmail.com',
    '@hotmail.com',
    '@outlook.com',
    '@yahoo.com'
  ];

  // Função para validar se é email corporativo
  const isPersonalEmail = (email: string): boolean => {
    const lowerEmail = email.toLowerCase();
    return personalEmailProviders.some(provider => lowerEmail.endsWith(provider));
  };

  // Função para validar email corporativo com melhorias
  const validateCorporateEmail = (email: string): { isValid: boolean; message: string; type: 'valid' | 'invalid' | 'personal' } => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    // Validação de formato básico
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Formato de email inválido', type: 'invalid' };
    }

    // Validação de email pessoal
    if (isPersonalEmail(email)) {
      return { 
        isValid: false, 
        message: 'Email pessoal detectado. Use um email corporativo (@suaempresa.com)', 
        type: 'personal'
      };
    }

    // Validação adicional: não deve ser apenas domínio
    const [user, domain] = email.split('@');
    if (!user || user.length < 2) {
      return { isValid: false, message: 'Usuário de email inválido', type: 'invalid' };
    }

    // Validação: domínio deve ter pelo menos 3 caracteres
    if (domain.length < 3 || !domain.includes('.')) {
      return { isValid: false, message: 'Domínio de email inválido', type: 'invalid' };
    }

    return { isValid: true, message: 'Email corporativo válido', type: 'valid' };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validar email quando o campo é alterado
    if (name === 'email' && value) {
      const validation = validateCorporateEmail(value);
      setEmailWarning(validation as any);
    } else if (name === 'email') {
      setEmailWarning('');
    }
  };

  // Verificar se o email é válido (corporativo)
  const isValidEmail = formData.email && typeof emailWarning === 'object' && emailWarning.type === 'valid';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.recaptchaToken || formData.recaptchaToken === '') {
      toast.error("Por favor, confirme que você não é um robô.");
      return;
    }

    // Validação básica
    if (!formData.nome || !formData.email) {
      toast.error('Por favor, preencha os campos obrigatórios.');
      return;
    }

    if(formData.telefone.length < 10 && formData.telefone.length > 0){
      toast.error('Por favor, insira um número de telefone válido.');
      return;
    }

    // Validação de email corporativo
    const emailValidation = validateCorporateEmail(formData.email);
    if (!emailValidation.isValid) {
      toast.error('⚠️ Por favor, utilize um email corporativo para continuar.');
      return;
    }

    setShowModal(true);
    await submitForm(formData);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (isSuccess) {
      setFormData({
        nome: '',
        telefone: '',
        email: '',
        empresa: '',
        servico: '',
        mensagem: '',
        recaptchaToken: ''
      });
    }
    resetForm();
  };

  return (
    <>
      <ToastContainer />
      <div className={`bg-white text-black rounded-lg shadow-lg p-6 ${className}`}>
        {title && (
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
            {subtitle && <p className="text-gray-600">{subtitle}</p>}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome *"
              name="nome"
              value={formData.nome}
              onChange={handleInputChange}
              placeholder="Seu nome completo"
              required
            />

            <PhoneNumberInput
              label="Telefone"
              name="telefone"
              value={formData.telefone}
              onChange={handleInputChange}
              placeholder="(00) 00000-0000"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                label="Email *"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="seu@empresa.com"
                required
              />
              {formData.email && emailWarning && typeof emailWarning === 'object' && emailWarning.type !== 'valid' && (
                <div className={`mt-2 p-3 rounded-md flex items-start gap-2 ${
                  emailWarning.type === 'personal'
                    ? 'bg-yellow-50 border border-yellow-300'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <div className={`flex-shrink-0 mt-0.5 ${
                    emailWarning.type === 'personal'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}>
                    {emailWarning.type === 'personal' ? (
                      <i className="ri-alert-line text-lg"></i>
                    ) : (
                      <i className="ri-close-circle-line text-lg"></i>
                    )}
                  </div>
                  <p className={`text-sm font-medium ${
                    emailWarning.type === 'personal'
                      ? 'text-yellow-700'
                      : 'text-red-700'
                  }`}>
                    {emailWarning.message}
                  </p>
                </div>
              )}
              <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                <i className="ri-information-line"></i>
                Use seu email corporativo. Emails pessoais não são aceitos.
              </p>
            </div>
            <Input
              label="Empresa"
              name="empresa"
              value={formData.empresa}
              onChange={handleInputChange}
              placeholder="Nome da empresa"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Serviço de Interesse
            </label>
            <select
              name="servico"
              value={formData.servico}
              onChange={handleInputChange}
              className="w-full text-black px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option className='color-black' value="">Selecione um serviço</option>
              <option value="Invictus TotalCare TI">Invictus TotalCare TI</option>
              <option value="Virtualização de Desktop com Citrix">Virtualização de Desktop com Citrix</option>
              <option value="Hospedagem de Sistemas Corporativos">Hospedagem de Sistemas Corporativos</option>
              <option value="Serviços de Data Center">Serviços de Data Center</option>
              <option value="Soluções de Segurança da Informação">Soluções de Segurança da Informação</option>
              <option value="Consultoria e Projetos de Infraestrutura">Consultoria e Projetos de Infraestrutura</option>
              <option value="Serviços Gerenciados (MSP)">Serviços Gerenciados (MSP)</option>
              <option value="Terceirização de TI">Terceirização de TI</option>
              <option value="Locação e Outsourcing">Locação e Outsourcing</option>
              <option value="Hardware">Hardware</option>
              <option value="Trabalho Remoto">Trabalho Remoto</option>
            </select>
            {/* subject preview */}
            <p className="mt-2 text-sm text-gray-500">Assunto: <span className="font-medium">{`[Site Invictus] ${formData.servico || 'Contato'}${formData.nome ? ' - ' + formData.nome : ''}`}</span></p>
          </div>

          <Textarea
            label="Mensagem"
            name="mensagem"
            className='text-black'
            value={formData.mensagem}
            onChange={handleInputChange}
            placeholder="Descreva sua necessidade ou dúvida..."
            rows={4}
            maxLength={500}
          />
          <ReCAPTCHA
            sitekey="6Ldn61IsAAAAAK5zzqmj7SnLF8FLipJ85z7x3daB"
            onChange={(token) => setFormData({...formData, recaptchaToken: token || ''})}
          />

          <Button
            type="submit"
            disabled={isLoading || !isValidEmail}
            aria-busy={isLoading}
            className={`w-full ${
              isLoading 
                ? 'bg-blue-400 cursor-wait' 
                : isValidEmail
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-gray-400 cursor-not-allowed opacity-60'
            } text-white font-semibold py-3 px-6 rounded-lg transition-colors whitespace-nowrap`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                Enviando...
              </>
            ) : (
              <>
                <i className="ri-send-plane-line mr-2"></i>
                Enviar Mensagem
              </>
            )}
          </Button>
        </form>
      </div>

      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title="Status do Envio"
      >
        <div className="text-center">
          {isLoading && (
            <div className="space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600">Enviando mensagem...</p>
            </div>
          )}

          {isSuccess && (
            <div className="space-y-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <i className="ri-check-line text-2xl text-green-600"></i>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-green-600 mb-2">Mensagem Enviada!</h4>
                <p className="text-gray-600">
                  Recebemos sua mensagem e entraremos em contato em breve.
                </p>
              </div>
            </div>
          )}

          {isError && (
            <div className="space-y-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-red-600 mb-2">Erro no Envio</h4>
                <p className="text-gray-600 mb-4">
                  {error || 'Ocorreu um erro ao enviar a mensagem. Tente novamente.'}
                </p>
                <Button
                  onClick={handleCloseModal}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded whitespace-nowrap"
                >
                  Tentar Novamente
                </Button>
              </div>
            </div>
          )}

          {(isSuccess || isError) && (
            <div className="mt-6">
              <Button
                onClick={handleCloseModal}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded whitespace-nowrap"
              >
                Fechar
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
